import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiDownload, FiBookOpen, FiFileText, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { templateLibraryAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';
import { formatDate } from '../../utils/dateFormat';
import { saveBlob } from '../../utils/fileDownload';
import './TemplateLibraryPicker.scss';

// Modal reached from the contract form's Contract Documents section: lists the
// ACTIVE templates defined for the form's selected Company + Contract Type and
// lets the user download one. Deliberately download-only — the intended flow
// is download → edit offline → upload the result as the draft contract.
function TemplateLibraryPicker({ open, onClose, companyId, contractTypeId }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const ready = !!(companyId && contractTypeId);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !ready) { setItems([]); return; }
    setLoading(true);
    templateLibraryAPI.list({ active: 'true', companyId, contractTypeId })
      .then(r => setItems(r.data.items || []))
      .catch(() => toast.error(t('templateLibrary.messages.loadFailed')))
      .finally(() => setLoading(false));
  }, [open, ready, companyId, contractTypeId, t]);

  const handleDownload = async (item) => {
    setDownloadingId(item._id);
    try {
      const res = await templateLibraryAPI.download(item._id);
      saveBlob(res.data, item.fileId?.name || item.name);
    } catch {
      toast.error(t('templateLibrary.messages.downloadFailed'));
    }
    setDownloadingId(null);
  };

  if (!open) return null;

  return (
    <div className="tlp-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="tlp" onClick={e => e.stopPropagation()}>
        <header className="tlp__header">
          <div className="tlp__title">
            <span className="tlp__title-icon"><FiBookOpen size={16} /></span>
            <div>
              <h3>{t('templateLibrary.pickerTitle')}</h3>
              <p>{t('templateLibrary.pickerSubtitle')}</p>
            </div>
          </div>
          <button className="tlp__close" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}>
            <FiX size={16} />
          </button>
        </header>

        <div className="tlp__body">
          {!ready ? (
            <div className="tlp__state">
              <FiInfo size={22} />
              <p>{t('templateLibrary.pickerSelectFirst')}</p>
            </div>
          ) : loading ? (
            <div className="tlp__state">
              <div className="tlp__spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : !items.length ? (
            <div className="tlp__state">
              <FiBookOpen size={22} />
              <p>{t('templateLibrary.pickerEmpty')}</p>
            </div>
          ) : (
            <ul className="tlp__list">
              {items.map(item => (
                <li key={item._id} className="tlp__row">
                  <span className="tlp__row-icon"><FiFileText size={15} /></span>
                  <div className="tlp__row-info">
                    <strong>{item.name}</strong>
                    <span>
                      {getLocalized(item.contractTypeId?.name, i18n.language)}
                      {' · '}{item.fileId?.name || '—'}
                      {' · v'}{item.version || 1}
                      {' · '}{formatDate(item.updatedAt)}
                    </span>
                  </div>
                  <button
                    className="tlp__download"
                    onClick={() => handleDownload(item)}
                    disabled={downloadingId === item._id}
                  >
                    <FiDownload size={14} />
                    {t('templateLibrary.download')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="tlp__footer">
          <FiInfo size={13} />
          <span>{t('templateLibrary.pickerFooter')}</span>
        </footer>
      </div>
    </div>
  );
}

export default TemplateLibraryPicker;
